/**
 * Max length of the optional "extra message" appended to a lock alert SMS.
 *
 * The alert SMS body is assembled server-side as: a derived label + this optional message + the
 * market snapshot (price, day change, and the 10:00/10:30/11:00 ET checkpoint lines). With all three
 * checkpoints present the worst-case base is ~110–115 chars, and a single GSM-7 SMS segment holds 160,
 * so capping the extra message at 35 keeps the whole alert inside one segment with buffer to spare.
 *
 * Note: this cap applies only to the lock extra message. Info alerts use `body` as the full
 * message and are not capped here.
 */
export const ALERT_EXTRA_MESSAGE_MAX_LENGTH = 35;

/**
 * GSM-7 default alphabet (single-septet characters). Any character outside this set (plus the
 * extension set below) forces the entire SMS into UCS-2 encoding, which cuts the per-segment size
 * from 160 to 70 chars — so a single emoji or em-dash can silently double the segment count (and cost)
 * of a ~20k-recipient blast.
 */
const GSM7_BASIC =
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡" +
    "ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

/** GSM-7 extension characters: legal in GSM-7, but each consumes two septets. */
const GSM7_EXTENSION = "\f^{}\\[~]|€";

const GSM7_ALLOWED = new Set([...GSM7_BASIC, ...GSM7_EXTENSION]);

/**
 * Common typographic characters mapped to their GSM-7 equivalents, so text pasted from word
 * processors (smart quotes, em/en dashes, ellipsis, non-breaking spaces) is preserved in a
 * readable form rather than dropped outright.
 */
const TYPOGRAPHIC_REPLACEMENTS: Record<string, string> = {
    "‘": "'",
    "’": "'",
    "‚": "'",
    "‛": "'",
    "′": "'",
    "“": '"',
    "”": '"',
    "„": '"',
    "‟": '"',
    "″": '"',
    "–": "-",
    "—": "-",
    "―": "-",
    "−": "-",
    "…": "...",
    " ": " ",
    " ": " ",
    " ": " ",
};

/**
 * Strip a string down to characters that are safe to send as a single-encoding GSM-7 SMS.
 * Common typographic characters are first normalized to their GSM-7 equivalents; anything still
 * outside the GSM-7 alphabet (emoji, other Unicode) is removed. Iterating by code point means
 * surrogate-pair emoji are handled as single units rather than mangled.
 */
export const sanitizeSmsText = (input: string): string => {
    let out = "";
    for (const char of input) {
        const mapped = TYPOGRAPHIC_REPLACEMENTS[char] ?? char;
        for (const c of mapped) {
            if (GSM7_ALLOWED.has(c)) {
                out += c;
            }
        }
    }
    return out;
};
